import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { format, parseISO, isValid } from "date-fns";
import { currentDateFnsLocale } from "../../../../i18n/dateLocales";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import { PMNotice } from "../../../utils/notifications";
import { PMButton } from "../../../utils/PMButton";
import PMListboxFormik from "../../../utils/PMListboxFormik";
import { FormikSaveBar } from "../../../utils/FormikSaveBar";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { axiosWithToast } from "../../../Utils";
import { fetchUserWorkSchedule, updateUserWorkSchedule } from "../../../../api/user";
import { fetchWorkSchedules } from "../../../../api/workSchedules";

const weekdayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export const UserWorkSchedule = ({ userItem, onRefresh }) => {
  const { t } = useTranslation();
  const isHired = userItem?.user_type_id === 3;
  const hasEmployment = Boolean(userItem?.employment?.id);
  const canAssign = isHired || hasEmployment;

  const [scheduleData, setScheduleData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [workScheduleOptions, setWorkScheduleOptions] = useState([]);

  const weekdayLabels = useMemo(
    () => ({
      monday: t("work_schedule_view.day.monday"),
      tuesday: t("work_schedule_view.day.tuesday"),
      wednesday: t("work_schedule_view.day.wednesday"),
      thursday: t("work_schedule_view.day.thursday"),
      friday: t("work_schedule_view.day.friday"),
      saturday: t("work_schedule_view.day.saturday"),
      sunday: t("work_schedule_view.day.sunday"),
    }),
    [t]
  );

  const loadSchedule = async () => {
    if (!userItem?.id || !canAssign) {
      setScheduleData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const yearStart = `${new Date().getFullYear()}-01-01`;
    const yearEnd = `${new Date().getFullYear()}-12-31`;
    const response = await fetchUserWorkSchedule(userItem.id, {
      date_from: yearStart,
      date_to: yearEnd,
    });

    const scaled = response?.scaled_weekdays ?? response?.weekdays;
    if (scaled && typeof scaled === "object" && !response?.error) {
      setScheduleData({
        ...response,
        scaled_weekdays: scaled,
        public_holidays: Array.isArray(response?.public_holidays) ? response.public_holidays : [],
      });
    } else {
      setScheduleData(null);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    fetchWorkSchedules()
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        setWorkScheduleOptions([
          { value: null, label: t("user_work_schedule.none") },
          ...rows.map((ws) => ({
            value: ws.id != null ? Number(ws.id) : null,
            label: ws.displayTitle || ws.name || `#${ws.id}`,
          })),
        ]);
      })
      .catch(() => {
        if (!cancelled) setWorkScheduleOptions([{ value: null, label: t("user_work_schedule.none") }]);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    loadSchedule();
  }, [userItem?.id, canAssign, userItem?.employment?.work_schedule_id]);

  const currentWorkScheduleId = useMemo(() => {
    if (scheduleData?.work_schedule_id != null) return Number(scheduleData.work_schedule_id);
    if (userItem?.employment?.work_schedule_id != null) {
      return Number(userItem.employment.work_schedule_id);
    }
    return null;
  }, [scheduleData, userItem]);

  const renderOverview = () => {
    if (isLoading) return <ResourceLoading />;

    if (!scheduleData?.scaled_weekdays || typeof scheduleData.scaled_weekdays !== "object") {
      return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm text-slate-700">{t("user_work_schedule.overview_empty")}</p>
          <p className="mt-2 text-xs text-slate-500">{t("user_work_schedule.overview_empty_hint")}</p>
        </div>
      );
    }

    const rows = weekdayKeys.map((day) => [day, scheduleData.scaled_weekdays[day]]);
    const holidays = (scheduleData.public_holidays || []).slice().sort((a, b) => {
      const ad = a.effective_date || a.date || "";
      const bd = b.effective_date || b.date || "";
      return ad.localeCompare(bd);
    });

    return (
      <div className="space-y-6">
        <div className="overflow-hidden rounded-lg bg-white shadow ring-1 ring-black/5">
          <div className="border-b border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-800">
              {scheduleData?.name || t("work_schedule_view.fallback_title")}
            </h3>
            <p className="text-sm text-gray-500">{t("user_work_schedule.overview_subtitle")}</p>
          </div>
          <div className="p-4">
            <table className="w-full table-fixed border-separate border-spacing-y-2">
              <tbody>
                {rows.map(([day, hours]) => (
                  <tr key={day} className="rounded-md bg-gray-50">
                    <td className="px-4 py-2 text-left font-medium text-gray-700">
                      {weekdayLabels[day] || day}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-900">
                      {Number(hours) > 0
                        ? t("work_schedule_view.hours_value", { hours })
                        : t("work_schedule_view.day_off")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow ring-1 ring-black/5">
          <div className="border-b border-red-100 bg-red-50 p-4">
            <h4 className="text-base font-semibold text-red-900">{t("work_schedule_view.holidays_title")}</h4>
            <p className="text-sm text-red-800">{t("work_schedule_view.holidays_subtitle")}</p>
          </div>
          <div className="p-4">
            {holidays.length === 0 ? (
              <p className="text-sm text-gray-500">{t("work_schedule_view.holidays_empty")}</p>
            ) : (
              <ul className="divide-y divide-red-100">
                {holidays.map((holiday) => {
                  const dateValue = holiday.effective_date || holiday.date;
                  const parsed = dateValue ? parseISO(String(dateValue).substring(0, 10)) : null;
                  const label =
                    parsed && isValid(parsed)
                      ? format(parsed, "d. MMMM yyyy", { locale: currentDateFnsLocale() })
                      : dateValue;
                  return (
                    <li
                      key={`${holiday.id || holiday.date}-${holiday.name}`}
                      className="flex items-center justify-between py-3"
                    >
                      <span className="text-sm font-medium text-gray-900">{holiday.name}</span>
                      <span className="text-sm text-gray-600">{label}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!canAssign) {
    return (
      <PMNotice
        severity="warning"
        title={t("user_work_schedule.not_available_title")}
        description={t("user_work_schedule.not_available_description")}
      />
    );
  }

  return (
    <div className="space-y-6 overflow-visible">
      <PMNotice
        severity="info"
        title={t("user_work_schedule.info_title")}
        description={
          isHired
            ? t("user_work_schedule.info_hired")
            : t("user_work_schedule.info_employee")
        }
      />

      <div className="relative z-20 overflow-visible rounded-lg bg-white shadow ring-1 ring-black/5">
        <div className="border-b border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900">{t("user_work_schedule.assign_title")}</h3>
          <p className="mt-1 text-sm text-gray-500">{t("user_work_schedule.assign_hint")}</p>
          <Link
            to="/dashboard/hours/work-schedules"
            className="mt-2 inline-block text-sm font-semibold text-indigo-600 hover:text-indigo-800"
          >
            {t("user_work_schedule.manage_link")}
          </Link>
        </div>
        <div className="overflow-visible p-4">
          <Formik
            enableReinitialize
            initialValues={{
              work_schedule_id: currentWorkScheduleId,
            }}
            onSubmit={(values, helpers) => {
              axiosWithToast(
                updateUserWorkSchedule(userItem.id, values.work_schedule_id),
                () => {
                  helpers.setSubmitting(false);
                  loadSchedule();
                  if (typeof onRefresh === "function") {
                    onRefresh({
                      ...userItem,
                      work_schedule_id: values.work_schedule_id,
                      employment: userItem.employment
                        ? { ...userItem.employment, work_schedule_id: values.work_schedule_id }
                        : userItem.employment,
                    });
                  }
                },
                () => helpers.setSubmitting(false),
                () => {},
                t("user_work_schedule.saved"),
                () => {
                  helpers.setSubmitting(false);
                  toast.error(t("user_work_schedule.save_error"));
                }
              );
            }}
          >
            {(formik) => (
              <Form>
                <PMListboxFormik
                  label={t("user_work_schedule.label")}
                  name="work_schedule_id"
                  options={workScheduleOptions}
                  {...formik}
                />
                <FormikSaveBar {...formik} />
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <div className="relative z-0">
        <h3 className="mb-3 text-lg font-semibold text-gray-900">{t("user_work_schedule.overview_title")}</h3>
        {renderOverview()}
      </div>
    </div>
  );
};
